import { type Style } from '@/domain/model';

/**
 * The weights a pair of axes is drawn in.
 *
 * ## Why these live together rather than in each painter
 *
 * Canvas, SVG and PDF all draw axes, and a figure that comes out of one button heavier than out
 * of another is the drift `ARCHITECTURE.md` warns about — the same reason `dashedIfCovered` is a
 * shared function rather than three agreeing implementations. Anything derived from a style has
 * to be derived once.
 *
 * ## Why one style was not enough
 *
 * Axes, ticks and numbers were all drawn in the entity's own style: one colour, one stroke width,
 * one text size. On screen that reads as flat; printed it reads as a grid, because at a
 * printer's resolution a 1px tick and a 1px axis are the same mark and the eye has nothing to
 * order them by.
 *
 * A textbook orders them, and the order is not arbitrary. The **axes** are the frame everything
 * else is read against, so they carry the most ink. The **ticks** are subordinate to the axis
 * they cross — they exist to locate a number, not to be seen. The **numbers** are quieter still,
 * because the curve is the subject and a scale that competes with it makes the figure harder to
 * read, not easier. The **letters** — `x`, `y`, `O` — come back up: they name parts of the figure
 * and a reader looks for them deliberately.
 *
 * Everything is derived from the entity's own style rather than fixed, so restyling the axes
 * still does what a user expects — the relationships hold, the palette is theirs.
 */

/** The two axes: the heaviest thing in the pair, because everything else is read against them. */
export function axisStroke(style: Style): Style {
  return { ...style, strokeWidth: style.strokeWidth * AXIS_WEIGHT };
}

/** Ticks: present enough to locate a number, quiet enough not to read as a grid. */
export function tickStroke(style: Style): Style {
  return { ...style, opacity: style.opacity * TICK_OPACITY };
}

/**
 * Tick numbers: a step down in size and in strength.
 *
 * Sized through `pointSize` because that is what `labelSize` reads, which is what keeps this in
 * step with every other piece of text in the drawing instead of introducing a second rule.
 */
export function numberInk(style: Style): Style {
  return {
    ...style,
    pointSize: style.pointSize * NUMBER_SIZE,
    opacity: style.opacity * NUMBER_OPACITY,
  };
}

/** `x`, `y` and `O`: named parts of the figure, at the same weight as any other label. */
export function nameInk(style: Style): Style {
  return style;
}

/**
 * How much heavier an axis is than what hangs off it.
 *
 * Enough to be seen at print resolution and not so much that the axes become the subject. Below
 * about a third the difference disappears in the rounding a printer does at hairline widths.
 */
const AXIS_WEIGHT = 1.4;

const TICK_OPACITY = 0.85;
const NUMBER_SIZE = 0.8;
const NUMBER_OPACITY = 0.8;
